import React, { useState, useRef } from "react";
import { PlusIcon, XIcon, TrashIcon } from "../icons";
import S from "../styles";

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const PRIORITY_COLORS = {
  Low: { bg: "rgba(90,138,181,0.1)", border: "rgba(10,10,15,0.58)", text: "rgba(10,10,15,0.58)" },
  Medium: { bg: "rgba(200,149,108,0.1)", border: "#0033FF", text: "#0033FF" },
  High: { bg: "rgba(249,115,22,0.1)", border: "#f97316", text: "#f97316" },
  Urgent: { bg: "rgba(239,68,68,0.15)", border: "#ef4444", text: "#ef4444" },
};

const STATUSES = ["Requested", "In Progress", "Finished"];
const STATUS_COLORS = {
  Requested: { bg: "rgba(200,149,108,0.1)", border: "#0033FF", text: "#0033FF" },
  "In Progress": { bg: "rgba(59,130,246,0.1)", border: "#3b82f6", text: "#3b82f6" },
  Finished: { bg: "rgba(34,197,94,0.1)", border: "#22c55e", text: "#22c55e" },
};

function KanbanCard({ item, onDelete, onDragStart }) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", item.id);
        onDragStart(item.id);
      }}
      style={{
        background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0,
        padding: "12px 14px", cursor: "grab", transition: "all .15s",
        marginBottom: 8,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(10,10,15,0.58)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#0A0A0F")}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0F" }}>{item.name}</span>
            {item.priority && item.priority !== "Low" && (() => {
              const pc = PRIORITY_COLORS[item.priority] || PRIORITY_COLORS.Medium;
              return <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 0, background: pc.bg, color: pc.text, border: `1px solid ${pc.border}30`, textTransform: "uppercase", letterSpacing: 0.5 }}>{item.priority}</span>;
            })()}
          </div>
          {item.quantity && (
            <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>
              Qty: <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{item.quantity}</span>
            </div>
          )}
          {item.notes && (
            <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginTop: 4, lineHeight: 1.4 }}>{item.notes}</div>
          )}
        </div>
        <button onClick={() => onDelete(item.id)} style={{ ...S.iconBtn, padding: 4, flexShrink: 0 }}><TrashIcon /></button>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
        {item.requestedBy && (
          <span style={{ fontSize: 11, color: "#0033FF", fontWeight: 600 }}>{item.requestedBy}</span>
        )}
        <span style={{ fontSize: 11, color: "rgba(10,10,15,0.58)", fontFamily: "'JetBrains Mono', monospace" }}>{item.date}</span>
      </div>
    </div>
  );
}

function KanbanColumn({ status, items, onDrop, onDelete, onDragStart }) {
  const [dragOver, setDragOver] = useState(false);
  const colors = STATUS_COLORS[status];

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const id = e.dataTransfer.getData("text/plain");
        if (id) onDrop(id, status);
      }}
      style={{
        flex: 1, minWidth: 240,
        background: dragOver ? "rgba(200,149,108,0.04)" : "#FFFFFF",
        border: `1px solid ${dragOver ? "#0033FF" : "#0A0A0F"}`,
        borderRadius: 0, padding: 12, transition: "all .2s",
        display: "flex", flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "0 4px" }}>
        <div style={{
          width: 10, height: 10, borderRadius: "50%",
          background: colors.border,
        }} />
        <span style={{ fontSize: 14, fontWeight: 600, color: colors.text, textTransform: "uppercase", letterSpacing: 1 }}>{status}</span>
        <span style={{
          fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
          background: colors.bg, color: colors.text, border: `1px solid ${colors.border}30`,
          borderRadius: 0, padding: "1px 8px", marginLeft: "auto",
        }}>{items.length}</span>
      </div>
      <div style={{ flex: 1, minHeight: 100 }}>
        {items.map((item) => (
          <KanbanCard key={item.id} item={item} onDelete={onDelete} onDragStart={onDragStart} />
        ))}
        {items.length === 0 && (
          <div style={{ textAlign: "center", padding: 20, color: "rgba(10,10,15,0.58)", fontSize: 13, border: "1px dashed 0A0A0F", borderRadius: 0 }}>
            Drop here
          </div>
        )}
      </div>
    </div>
  );
}

function KanbanBoard({ title, items, setItems }) {
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", quantity: "", notes: "", priority: "Medium" });
  // eslint-disable-next-line no-unused-vars
  const [dragId, setDragId] = useState(null);
  const nameRef = useRef(null);

  const addItem = () => {
    if (!newItem.name.trim()) return;
    let userName = "";
    try { const u = JSON.parse(sessionStorage.getItem("nycmfg-user")); userName = u?.name || ""; } catch {}
    const item = {
      id: String(Date.now()),
      name: newItem.name.trim(),
      quantity: newItem.quantity.trim(),
      notes: newItem.notes.trim(),
      priority: newItem.priority || "Medium",
      status: "Requested",
      requestedBy: userName,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
    };
    setItems([...items, item]);
    setNewItem({ name: "", quantity: "", notes: "", priority: "Medium" });
    setAdding(false);
  };

  const deleteItem = (id) => setItems(items.filter((i) => i.id !== id));

  const moveItem = (id, newStatus) => {
    setItems(items.map((i) => (i.id === id ? { ...i, status: newStatus } : i)));
    setDragId(null);
  };

  const grouped = {};
  STATUSES.forEach((s) => (grouped[s] = []));
  items.forEach((i) => {
    const status = STATUSES.includes(i.status) ? i.status : "Requested";
    grouped[status].push(i);
  });

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ fontSize: 17, fontWeight: 600, color: "#0A0A0F" }}>{title}</h3>
        <button onClick={() => { setAdding(!adding); setTimeout(() => nameRef.current?.focus(), 50); }} style={S.addBtn}>
          {adding ? <XIcon /> : <PlusIcon />} {adding ? "Cancel" : "New Request"}
        </button>
      </div>

      {adding && (
        <div style={{
          background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0,
          padding: 16, marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap",
        }}>
          <div style={{ flex: 2, minWidth: 180 }}>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 }}>Item Name *</label>
            <input ref={nameRef} value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
              placeholder="e.g. Bubble Mailers" style={{ ...S.input, marginBottom: 0, fontSize: 14 }} />
          </div>
          <div style={{ flex: 1, minWidth: 80 }}>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 }}>Quantity</label>
            <input value={newItem.quantity} onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
              placeholder="e.g. 50" style={{ ...S.input, marginBottom: 0, fontSize: 14 }} />
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 }}>Priority</label>
            <select
              value={newItem.priority}
              onChange={(e) => setNewItem({ ...newItem, priority: e.target.value })}
              style={{ ...S.input, marginBottom: 0, fontSize: 14, cursor: "pointer", color: (PRIORITY_COLORS[newItem.priority] || {}).text || "#0A0A0F" }}
            >
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div style={{ flex: 2, minWidth: 180 }}>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 }}>Notes</label>
            <input value={newItem.notes} onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
              placeholder="Optional details" style={{ ...S.input, marginBottom: 0, fontSize: 14 }} />
          </div>
          <button onClick={addItem} disabled={!newItem.name.trim()} style={{
            padding: "10px 20px", borderRadius: 0, border: "none", fontSize: 14, fontWeight: 600,
            fontFamily: "'Archivo', sans-serif", cursor: newItem.name.trim() ? "pointer" : "default",
            background: newItem.name.trim() ? "#0033FF" : "#0A0A0F",
            color: newItem.name.trim() ? "#FFFFFF" : "rgba(10,10,15,0.58)",
          }}>
            Add
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: 12 }}>
        {STATUSES.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            items={grouped[status]}
            onDrop={moveItem}
            onDelete={deleteItem}
            onDragStart={setDragId}
          />
        ))}
      </div>
    </div>
  );
}

export default function StoreRequestsPage({ storeName, requests, setRequests }) {
  const inventoryRequests = requests.filter((r) => r.type === "inventory");
  const hardwareRequests = requests.filter((r) => r.type === "hardware");

  const setInventory = (items) => {
    const tagged = items.map((i) => ({ ...i, type: "inventory" }));
    setRequests([...tagged, ...hardwareRequests]);
  };

  const setHardware = (items) => {
    const tagged = items.map((i) => ({ ...i, type: "hardware" }));
    setRequests([...inventoryRequests, ...tagged]);
  };

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>{storeName} — Requests</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 24 }}>Track inventory and hardware requests through the pipeline</p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}><span style={S.statLabel}>Total Requests</span><span style={S.statValue}>{requests.length}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Requested</span><span style={{ ...S.statValue, color: "#0033FF" }}>{requests.filter((r) => r.status === "Requested").length}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>In Progress</span><span style={{ ...S.statValue, color: "#3b82f6" }}>{requests.filter((r) => r.status === "In Progress").length}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Finished</span><span style={{ ...S.statValue, color: "#22c55e" }}>{requests.filter((r) => r.status === "Finished").length}</span></div>
      </div>

      <KanbanBoard title="Inventory Requests" items={inventoryRequests} setItems={setInventory} />
      <KanbanBoard title="Hardware Requests" items={hardwareRequests} setItems={setHardware} />
    </div>
  );
}
